import { faBolt, faClock, faHistory } from '@fortawesome/free-solid-svg-icons'
import { ReactNode, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useKillRaceActive } from '../api/useKillRaceActive'
import { useKillRaceHistory } from '../api/useKillRaceHistory'
import { RaceView, useRaceView } from '../hooks/useRaceView'
import { KillRace, KillRaceLog } from '../types'
import { coarseDuration, formatUsd, resolvePhase, secondsUntil, toNumber } from '../utils/killrace'
import { KillRaceCard, KillRaceCardSkeleton } from './KillRaceCard'
import { HistoryPanel } from './KillRaceHistory'
import { KillRaceRow, KillRaceRowHeader, KillRaceRowSkeleton } from './KillRaceRow'
import { EmptyBlock, ErrorBlock, Panel } from './KillRaceUI'
import { RaceViewToggle } from './RaceViewToggle'
import { TabDef, TabKey, TournamentTabs } from './TournamentTabs'

/**
 * Two up, except when there is only one race — a lone half-width card leaves the other half of a
 * 1440px screen blank, which is most of what made the old two-section layout look unfinished.
 */
const Grid = ({ solo = false, children }: { solo?: boolean; children: ReactNode }) => (
    <div className={twMerge('grid grid-cols-1 gap-4 md:gap-6', !solo && 'md:grid-cols-2')}>{children}</div>
)

/**
 * The list view's container.
 *
 * One panel around the whole run of rows rather than a panel per race: sixteen notched outlines
 * stacked at 52px apart is more chrome than content, and it makes the list read as the same class
 * of object as the Results table, which it is.
 */
const List = ({ live, children }: { live?: boolean; children: ReactNode }) => (
    <Panel className='flex flex-col gap-2 p-3 md:p-4'>
        {live !== undefined && <KillRaceRowHeader live={live} />}
        {children}
    </Panel>
)

/**
 * The whole race browser: one tab bar over one grid — or over one list, at the viewer's choice.
 *
 * Both queries are fired here rather than inside the selected branch, because the tab labels carry
 * counts for buckets that are not on screen — a count that only appears once you click the tab is
 * not a reason to click it. React Query dedupes by key, so the hero reading `useKillRaceActive`
 * costs nothing extra.
 */
export const KillRaceBrowser = () => {
    const { data, isLoading, isError, refetch } = useKillRaceActive()
    const history = useKillRaceHistory()
    const { view, setView } = useRaceView()

    // `null` means "the user has not chosen" — the resolved tab then follows the data, so a page
    // with nothing live opens on Upcoming instead of on an empty state. Once they pick, the pick
    // sticks.
    const [picked, setPicked] = useState<TabKey | null>(null)

    // The endpoint returns a bare array; anything else means the response was not what we expect,
    // and `.map` on it would take the whole page down.
    const races: KillRace[] = Array.isArray(data) ? data : []
    const rows: KillRaceLog[] = Array.isArray(history.data) ? history.data : []

    const live = races.filter((race) => resolvePhase(race.phase, race.begin, race.end) === 'Live')
    const upcoming = races
        .filter((race) => resolvePhase(race.phase, race.begin, race.end) === 'Upcoming')
        .sort((a, b) => new Date(a.begin).getTime() - new Date(b.begin).getTime())

    const pool = live.reduce((sum, race) => sum + toNumber(race.prize), 0)
    const earned = rows.reduce((sum, row) => sum + toNumber(row.earnings), 0)
    const nextStart = secondsUntil(upcoming[0]?.begin)

    const tabs: TabDef[] = [
        {
            key: 'live',
            icon: faBolt,
            label: 'Live',
            count: live.length,
            note: pool > 0 ? `${formatUsd(pool, 0)} pool` : undefined,
        },
        {
            key: 'upcoming',
            icon: faClock,
            label: 'Upcoming',
            count: upcoming.length,
            note: nextStart !== null ? `next in ${coarseDuration(nextStart)}` : undefined,
        },
        {
            key: 'results',
            icon: faHistory,
            label: 'Results',
            count: rows.length,
            note: earned > 0 ? `${formatUsd(earned)} won` : undefined,
        },
    ]

    const auto: TabKey =
        live.length > 0 ? 'live' : upcoming.length > 0 ? 'upcoming' : rows.length > 0 ? 'results' : 'live'
    const selected = picked ?? auto

    // Results has its own loading and error states inside `HistoryPanel`, and reads a different
    // query, so the active query's states must not speak for it.
    const body = () => {
        if (selected === 'results') return <HistoryPanel />

        if (isError) {
            return (
                <ErrorBlock title="Couldn't load tournaments" onRetry={refetch}>
                    We could not reach the tournament service. Your progress is unaffected, and nothing is recorded
                    from this page.
                </ErrorBlock>
            )
        }

        if (isLoading) {
            return view === 'list' ? (
                <List>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <KillRaceRowSkeleton key={i} />
                    ))}
                </List>
            ) : (
                <Grid>
                    <KillRaceCardSkeleton />
                    <KillRaceCardSkeleton />
                </Grid>
            )
        }

        if (selected === 'upcoming') {
            return (
                <Bucket
                    races={upcoming}
                    view={view}
                    live={false}
                    empty='Nothing scheduled yet'
                    body='No races are lined up at the moment. New ones are added regularly and appear here as soon as they are.'
                />
            )
        }

        return (
            <Bucket
                races={live}
                view={view}
                live
                empty='No live kill races'
                body={
                    nextStart !== null
                        ? `Nothing is running right now. The next race opens in ${coarseDuration(nextStart)} — get a kill once it does and you are entered automatically.`
                        : 'Nothing is running right now, and none is scheduled yet. Races are added regularly, so check back soon, or look over your results.'
                }
            />
        )
    }

    return (
        <section className='flex w-full flex-col gap-4 md:gap-5'>
            {/*
              The toggle rides at the end of the tab row from `md`, where there is room to spare
              beside three 10rem tabs. Below that it drops to its own line, right-aligned: three
              tabs across a 375px screen already leave "UPCOMING" barely enough width, and taking
              88px out of that row to park a control truncates the labels that name the tabs. A
              52px band is a cheap price for a control that removes thousands of pixels of scroll.
            */}
            <div className='flex flex-col gap-2 md:flex-row md:items-start md:gap-4'>
                <TournamentTabs tabs={tabs} selected={selected} onSelect={setPicked} className='md:flex-1' />
                {/* Results is already a table; there is nothing there to switch. */}
                {selected !== 'results' && (
                    <RaceViewToggle view={view} onChange={setView} className='self-end md:self-auto' />
                )}
            </div>
            {body()}
        </section>
    )
}

const Bucket = ({
    races,
    view,
    live,
    empty,
    body,
}: {
    races: KillRace[]
    view: RaceView
    live: boolean
    empty: string
    body: string
}) => {
    if (races.length === 0) return <EmptyBlock title={empty}>{body}</EmptyBlock>

    if (view === 'list') {
        return (
            <List live={live}>
                {races.map((race) => (
                    <KillRaceRow key={race.uuid} race={race} />
                ))}
            </List>
        )
    }

    return (
        <Grid solo={races.length === 1}>
            {races.map((race, i) => (
                <KillRaceCard key={race.uuid} race={race} index={i} featured={races.length === 1} />
            ))}
        </Grid>
    )
}
